import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiUsers, FiTrendingUp, FiShield, FiCheck } from 'react-icons/fi';
import { trackLead } from '../../utils/metaPixel';
import { captureUTMParams, getStoredUTM } from '../../utils/utm';
import api from '../../utils/api';
import './MetaLandingPage.css';

const BRAND_LOGOS = [
  'lacoste.png', 'decathlon.png', 'elite.png', 'timberland.png',
  'toysrus.png', 'clalit.png', 'mega_sport.png', 'harley_davidson.png',
  'twix.png', 'similac.png', 'yes.png', 'ellesse.png'
];

const VALUE_PROPS = [
  {
    icon: FiUsers,
    title: 'יוצרים אמיתיים',
    text: 'גישה ליוצרי תוכן מוכשרים שמכירים את הקהל שלהם'
  },
  {
    icon: FiTrendingUp,
    title: 'תוצאות מדידות',
    text: 'ניהול קמפיינים מקצה לקצה עם דוחות ביצועים'
  },
  {
    icon: FiShield,
    title: 'ביטחון מלא',
    text: 'תהליך מאובטח עם שקיפות מלאה בכל שלב'
  }
];

export default function MetaLandingPage() {
  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState('');

  useEffect(() => {
    captureUTMParams();
  }, []);

  const validate = () => {
    const errs = {};
    if (!fullName.trim()) errs.fullName = 'שם מלא הוא שדה חובה';
    if (!phone.trim()) {
      errs.phone = 'מספר טלפון הוא שדה חובה';
    } else if (!/^0[0-9]{8,9}$/.test(phone.trim())) {
      errs.phone = 'מספר טלפון לא תקין';
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');
    if (!validate()) return;

    setIsSubmitting(true);
    try {
      await api.post('/leads', {
        fullName: fullName.trim(),
        phone: phone.trim(),
        source: 'meta_landing',
        ...getStoredUTM()
      });
      trackLead({ content_name: 'meta_landing' });
      setSubmitted(true);
    } catch (err) {
      setSubmitError(err.response?.data?.error || 'שגיאה בשליחה, נסו שוב');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="meta-lp" dir="rtl">
      {/* Logo */}
      <div className="meta-lp__logo-bar">
        <img src="/logo-white.png" alt="Weon" className="meta-lp__logo" />
      </div>

      {/* Hero */}
      <section className="meta-lp__hero">
        <motion.h1
          className="meta-lp__headline"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          אנשים קונים מאנשים,
          <br />
          <span className="meta-lp__highlight">לא מפרסומות</span>
        </motion.h1>

        <motion.p
          className="meta-lp__subheadline"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.15 }}
        >
          הגיע הזמן שהלקוחות שלך ישמעו על המותג מאנשים שהם סומכים עליהם
        </motion.p>

        {/* Form / Thank You */}
        <AnimatePresence mode="wait">
          {!submitted ? (
            <motion.form
              key="form"
              className="meta-lp__form"
              onSubmit={handleSubmit}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              <div className="meta-lp__field">
                <input
                  type="text"
                  placeholder="שם מלא"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  className={errors.fullName ? 'meta-lp__input meta-lp__input--error' : 'meta-lp__input'}
                  autoComplete="name"
                />
                {errors.fullName && <span className="meta-lp__error">{errors.fullName}</span>}
              </div>

              <div className="meta-lp__field">
                <input
                  type="tel"
                  placeholder="טלפון"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className={errors.phone ? 'meta-lp__input meta-lp__input--error' : 'meta-lp__input'}
                  autoComplete="tel"
                  inputMode="numeric"
                />
                {errors.phone && <span className="meta-lp__error">{errors.phone}</span>}
              </div>

              {submitError && <p className="meta-lp__submit-error">{submitError}</p>}

              <button
                type="submit"
                className="meta-lp__cta"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'שולח...' : 'רוצה לשמוע עוד'}
              </button>
            </motion.form>
          ) : (
            <motion.div
              key="thanks"
              className="meta-lp__thanks"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
            >
              <div className="meta-lp__thanks-icon"><FiCheck /></div>
              <h2>תודה רבה!</h2>
              <p>קיבלנו את הפרטים שלך וניצור קשר בהקדם</p>
            </motion.div>
          )}
        </AnimatePresence>
      </section>

      {/* Value Props */}
      <section className="meta-lp__values">
        {VALUE_PROPS.map((prop, i) => (
          <motion.div
            key={i}
            className="meta-lp__value-card"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-50px' }}
            transition={{ duration: 0.5, delay: i * 0.1 }}
          >
            <div className="meta-lp__value-icon"><prop.icon /></div>
            <h3>{prop.title}</h3>
            <p>{prop.text}</p>
          </motion.div>
        ))}
      </section>

      {/* Trust Logos */}
      <section className="meta-lp__trust">
        <p className="meta-lp__trust-label">מותגים שכבר עובדים איתנו</p>
        <div className="meta-lp__logos">
          {BRAND_LOGOS.map((logo) => (
            <img
              key={logo}
              src={`/brands/${logo}`}
              alt=""
              className="meta-lp__brand-logo"
              loading="lazy"
            />
          ))}
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="meta-lp__bottom-cta">
        <p>מוכנים להתחיל?</p>
        <button
          className="meta-lp__cta"
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        >
          השאירו פרטים
        </button>
      </section>
    </div>
  );
}
